import { PrimaryButton } from '@components/Buttons';
import { InlineBlock, InlineFlex } from '@components/Layout/Blocks';
import { Row } from '@components/Layout/Row';
import { isProduction } from '@lib/utils';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grow from '@material-ui/core/Grow';
import Typography from '@material-ui/core/Typography';
import { call } from '@packages/functions';
import { IF } from '@packages/react/lib/if';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { I18n } from './Trans';

const PageLoading = styled.div`
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(3px);
    z-index: 1180;
    display: flex;
    align-content: center;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    @media screen and (max-width: 499px) {
        flex-direction: column;
    }

    .inner,
    .loader__holder,
    .img {
        z-index: 1;
        font-size: 0;
    }

    .loader__holder {
        display: inline-block;
        font-size: 0;
        z-index: 3;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    .inner {
        position: relative;
        display: inline-block;
        border-radius: 50%;
        box-shadow: 0 0 20px rgba(0, 0, 0, 0.15);
        width: 64px;
        height: 64px;
    }

    .error-content {
        @media screen and (max-width: 499px) {
            align-content: center;
            align-items: center;
            justify-content: center;
        }
    }
`;

const ErrorRetry = React.memo<any>(({ error, retry, variant, pastDelay, timedOut, isLoading, ...rest }) => {
    const [message, setMessage] = useState<string>('Ooops! Something went wrong!');
    const hasError = !!error;

    const handleRetry = useCallback(() => call(retry), [retry]);

    useEffect(() => {
        if (error && !isProduction()) {
            console.error(error);
        }

        if (hasError) {
            // eslint-disable-next-line
            switch (true) {
                case /loading chunk/i.test(error.message):
                    setMessage(`Error loading requested ${variant || 'page'}!`);
                    break;
                default:
                    if (!isProduction()) {
                        setMessage(error.message);
                    }
            }
        }
    }, [error]);

    return (
        <Grow in={hasError} unmountOnExit>
            <InlineFlex
                wrap
                className={'error-content'}
                padding={14}
                layout={'column'}
                align={'start'}
                justify={'center'}
                minWidth={320}
                {...rest}
            >
                {IF(
                    !isProduction() && !!error,
                    <InlineBlock o={'auto'} w={'100%'}>
                        <pre>{(error || {}).stack}</pre>
                    </InlineBlock>,
                )}
                <Typography variant={'subtitle2'} color={'error'}>
                    {message}
                </Typography>
                {IF(
                    !!retry,
                    <PrimaryButton color={'error'} onClick={handleRetry} style={{ margin: 8 }}>
                        Retry
                    </PrimaryButton>,
                )}
            </InlineFlex>
        </Grow>
    );
});

const _canRender = (pastDelay) => pastDelay || typeof pastDelay === 'undefined';

export const Loading = React.memo<any>((props) => {
    // Can render?
    if (!props.error && !_canRender(props.pastDelay)) {
        return null;
    }

    return (
        <PageLoading>
            <div className={'inner'}>
                {IF(
                    !props.error,
                    <span className={'loader__holder'}>
                        <CircularProgress color={'primary'} size={34} thickness={2} />
                    </span>,
                )}
            </div>
            <ErrorRetry variant={'page'} {...props} />
        </PageLoading>
    );
});

export const LoadingComponent = Loading;

export const ComponentLoader = React.memo<any>(({ pastDelay, error, isLoading, timedOut, retry, ...rest }) => {
    // Can render?
    if (!error && !_canRender(pastDelay)) {
        return null;
    }

    return IF(
        !!error,
        <ErrorRetry
            variant={'element'}
            error={error}
            retry={retry}
            layout={'row-reverse'}
            align={'center'}
            justify={'start'}
            {...rest}
        />,
        <Row padding={14} align={'center'} justify={'start'} {...rest}>
            <CircularProgress color={'primary'} size={24} />
            <I18n style={{ marginLeft: 8 }} uppercase variant={'body1'} after={'...'}>
                Loading
            </I18n>
        </Row>,
    );
});
